import type {ReactNode} from 'react'
import {useState, useRef, useCallback, useEffect} from 'react'
import {clsx} from 'clsx'
import {DashIcon, ChevronRightIcon} from '@primer/octicons-react'
import styles from './UserChoiceCard.module.css'

export interface UserChoiceCardProps {
  /** Card title (shown in header) — supports ReactNode for markdown */
  title: ReactNode
  /** Optional subtitle (muted, shown after title) */
  subtitle?: ReactNode
  /** Additional buttons rendered in the header row (e.g. fullscreen icon) */
  headerButtons?: ReactNode
  /** Preview content rendered above choices (e.g. plan markdown, command detail) */
  previewContent?: ReactNode
  /** Choice rows — typically ChoiceButtonGroup */
  children?: ReactNode
  /** Footer buttons below choices (e.g. Submit, Skip) */
  footerButtons?: ReactNode
  /** When true, hides container chrome (border, background) for embedding in another container */
  embedded?: boolean
  /** Whether the card content is expanded */
  isExpanded?: boolean
  /** Callback when expand/collapse is toggled */
  onToggle?: () => void
}

/** Shared card wrapper for active interactive user prompts */
export function UserChoiceCard({
  title,
  subtitle,
  headerButtons,
  previewContent,
  children,
  footerButtons,
  embedded = false,
  isExpanded: isExpandedProp,
  onToggle,
}: UserChoiceCardProps) {
  const [isExpandedInternal, setIsExpandedInternal] = useState(true)
  const isControlled = isExpandedProp !== undefined
  const isExpanded = isControlled ? isExpandedProp : isExpandedInternal

  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)
  const [canScroll, setCanScroll] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setIsScrolled(el.scrollTop > 0)
    setCanScroll(el.scrollHeight > el.clientHeight)
    const SCROLL_TOLERANCE = 1
    setIsScrolledToBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_TOLERANCE)
  }, [])

  useEffect(() => {
    checkScroll()
  }, [checkScroll, isExpanded])

  const handleToggle = () => {
    if (!isControlled) {
      setIsExpandedInternal(prev => !prev)
    }
    onToggle?.()
  }

  const headerContent = !embedded ? (
    <div className={styles.header}>
      <div className={styles.headerText}>
        <div className={styles.title}>{title}</div>
        {subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null}
      </div>
      <div className={styles.headerActions}>
        {headerButtons}
        <button
          type="button"
          className={styles.collapseButton}
          onClick={handleToggle}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
        >
          {isExpanded ? <DashIcon size={16} /> : <ChevronRightIcon size={16} />}
        </button>
      </div>
    </div>
  ) : null

  return (
    <div className={clsx(styles.card, embedded && styles.embedded)}>
      {/* Collapsed: show header only */}
      {!isExpanded && headerContent}
      {/* Expanded: scrollable area wraps header + preview + children; only footerButtons pinned */}
      {isExpanded && (
        <>
          <div ref={scrollRef} className={styles.scrollArea} onScroll={checkScroll}>
            {canScroll && isScrolled ? <div className={styles.gradientTop} /> : null}
            {headerContent}
            {previewContent ? <div className={styles.previewContent}>{previewContent}</div> : null}
            {children ? <div className={styles.childrenContent}>{children}</div> : null}
            {canScroll && !isScrolledToBottom ? <div className={styles.gradientBottom} /> : null}
          </div>
          {footerButtons ? (
            <div className={clsx(styles.footer, canScroll && styles.footerBorder)}>
              <div className={styles.footerButtons}>{footerButtons}</div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
